import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, 
  Building2, HardHat, FileText, CheckSquare, Layers, MapPin
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface ConstructionCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const CONSTRUCTION_AGENT_IDS = [
  'construction-hvac-energy-passport',
  'koshtorisnyk',
  'construction-acts-auditor-kb2-kb3',
  'construction-estimate-parser-avk',
  'construction-pto-work-log-auditor',
  'construction-bim-ifc-boq-extractor',
  'construction-diam-e-system-auditor',
  'construction-war-damage-evaluator',
  'construction-dpt-urban-planner',
  'urban-masterplan-architect',
  'war-damage-sat-auditor',
  'bim-clash-detector',
  'construction-safety-inspector',
  'building-thermal-auditor',
  'pto-work-log-inspector',
  'ekw-rejestr-zastawow-auditor',
  'rd4u-war-loss-claims-auditor',
  'construction-electrical-engineer'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <HardHat className={className} />;
  return <IconComponent className={className} />;
};

export default function ConstructionCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: ConstructionCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch construction pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const agents = agentsConfig.filter((a: any) => CONSTRUCTION_AGENT_IDS.includes(a.id));

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-sky-500/20 text-sky-300 border border-sky-400/30 rounded-md">
            {isPl ? '🏗️ 7 Modułów Budowlano-Inżynieryjnych' : isUk ? '🏗️ 7 Будівельно-Інженерних Агентів' : '🏗️ 7 Construction & Engineering Agents'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-orange-500/20 text-orange-300 border border-orange-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-orange-400" />
            {isPl ? 'KNR • Sekocenbud • Prawo Budowlane • FIDIC' : isUk ? 'ПУЕ • ДБН В.2.5-23 • ДІАМ • ЄДЕССБ • ДСТУ 4163' : 'Building Codes & Standards Compliance'}
          </span>
        </div>
      </div>

      <div className="relative bg-gradient-to-r from-slate-950 via-sky-950 to-slate-900 border border-sky-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-sky-500/30 text-sky-300 border border-sky-400/40 rounded">
              {isPl ? 'BUDOWNICTWO • KOSZTORYSOWANIE KNR • BIM • AUDYT ZNISZCZEŃ' : isUk ? 'ЦЕНТР БУДІВНИЦТВА, ДІАМ, ДПТ ТА ВІДБУДОВИ' : 'CONSTRUCTION & INFRASTRUCTURE RECOVERY'}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide text-white">
            {isPl ? '🏗️ CENTRUM BUDOWNICTWA, KOSZTORYSÓW & ODBUDOWY' : isUk ? '🏗️ ЦЕНТР БУДІВНИЦТВА, ДІАМ & ВІДБУДОВИ' : '🏗️ CONSTRUCTION & RECONSTRUCTION HUB'}
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Wielomodułowy audyt dokumentacji projektowo-kosztorysowej KNR/Sekocenbud, detekcja kolizji BIM, szacowanie szkód wojennych i koncepcje planowania przestrzennego.'
              : isUk
              ? 'Мультиагентний аудит проєктно-кошторисної документації ДІАМ та ЄДЕССБ, оцінка руйнувань та збитків від бойових дій (ПКМУ № 423) і містобудівне розроблення концепцій ДПТ.'
              : 'Multi-agent audit of design and cost estimation documentation, war damage assessment, BIM clash detection, and urban master plan concepts.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="bg-slate-950 border border-sky-500/30 hover:border-sky-400 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all hover:scale-[1.01] shadow-xl group"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 text-[9px] font-extrabold uppercase bg-sky-500/20 text-sky-300 border border-sky-400/30 rounded">
                    {getLocalizedAgentCategory(agent, language)}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
                    ✨ Gemini 3.7 Flash
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded font-mono">
                    {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                  </span>
                  {singlePriceVal > 0 && (
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded font-mono">
                      {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-[9px] font-bold text-amber-300 bg-amber-950/60 rounded border border-amber-500/30">
                    {isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}
                  </span>
                </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-sky-950/80 border border-sky-500/30 rounded-xl text-sky-400 shrink-0 group-hover:text-sky-300 transition-colors">
                  <AgentIcon name={agent.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-white uppercase group-hover:text-sky-300 transition-colors leading-snug">
                    {getLocalizedAgentName(agent, language)}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 font-mono leading-relaxed line-clamp-3">
                    {getLocalizedAgentDesc(agent, language)}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-slate-800">
                {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-300 font-mono">
                    <ShieldCheck className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => onLaunchAgent(agent.id)}
                className="w-full py-3 bg-sky-600 hover:bg-sky-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>{isPl ? '🚀 Otwórz Moduł Budowlany' : isUk ? '🚀 Відкрити Будівельний Модуль' : '🚀 Launch Construction Module'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
